import { type MouseEvent, memo, useCallback } from 'react';

import { PauseIcon, PlayIcon } from '@air/design-icons';
import { IconButton, type IconButtonProps } from '@air/primitive-icon-button';

import { usePlayerPlayPause } from '../hooks/usePlayerPlayPause';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerPlayButtonProps = Omit<IconButtonProps, 'children' | 'label'>;

export const PlayerPlayButton = memo(({ onClick, ...restOfProps }: PlayerPlayButtonProps) => {
  const videoElement = usePlayerStore((state) => state.videoElement);
  const { onPlayerPlayPause, playing } = usePlayerPlayPause();

  const handleTogglePlay = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onPlayerPlayPause();

      onClick?.(event);
    },
    [onClick, onPlayerPlayPause],
  );

  return (
    <IconButton
      appearance="ghost"
      color="gray"
      data-testid="PLAYER_PLAY_BUTTON"
      disabled={!videoElement}
      label={playing ? 'Pause' : 'Play'}
      onClick={handleTogglePlay}
      {...restOfProps}
    >
      {playing ? <PauseIcon size="sm" /> : <PlayIcon className="fill-current" size="sm" />}
    </IconButton>
  );
});

PlayerPlayButton.displayName = 'PlayerPlayButton';
